import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useConsent } from "@/consent/ConsentContext";
import { EASE } from "@/components/story/Reveal";

const CATEGORIES = [
  {
    key: "necessary",
    name: "Technisch notwendig",
    locked: true,
    desc: "Erforderlich für den Betrieb der Website und die Verwaltung Ihrer Datenschutz-Einwilligung. Diese Speichervorgänge können nicht deaktiviert werden.",
    details: [
      ["Technologie", "localStorage-Eintrag \u201esypama_consent\u201c (kein Cookie-Tracking)"],
      ["Zweck", "Speicherung und rechtlicher Nachweis Ihrer Einwilligung"],
      ["Speicherdauer", "12 Monate oder bis zum Widerruf"],
    ],
  },
  {
    key: "statistics",
    name: "Statistik & Analyse",
    locked: false,
    desc: "Derzeit werden auf dieser Website keine Statistik- oder Analyse-Dienste eingesetzt. Sollten künftig entsprechende Dienste hinzukommen, werden sie erst nach Ihrer aktiven Einwilligung geladen.",
    details: [
      ["Anbieter", "Derzeit keine"],
      ["Zweck", "Reichweitenmessung (nur falls zukünftig aktiviert)"],
      ["Speicherdauer", "—"],
    ],
  },
  {
    key: "marketing",
    name: "Marketing & Advertising",
    locked: false,
    desc: "Derzeit werden keine Marketing-, Werbe- oder Tracking-Technologien eingesetzt.",
    details: [
      ["Anbieter", "Derzeit keine"],
      ["Zweck", "Werbliche Ausspielung (nur falls zukünftig aktiviert)"],
      ["Speicherdauer", "—"],
    ],
  },
  {
    key: "externalMedia",
    name: "Externe Medien",
    locked: false,
    desc: "Derzeit werden keine externen Medien (Videos, Karten o. Ä.) eingebettet. Alle Schriftarten und Bilder werden lokal vom eigenen Server geladen; es findet keine Verbindung zu Drittanbietern wie Google Fonts statt.",
    details: [
      ["Anbieter", "Derzeit keine"],
      ["Zweck", "Einbettung externer Inhalte (nur falls zukünftig aktiviert)"],
      ["Speicherdauer", "—"],
    ],
  },
];

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      data-testid={testid}
      onClick={() => !disabled && onChange(!on)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
        on ? "bg-[#D4AF37]" : "bg-slate-300"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${
          on ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

const BTN =
  "f-mono rounded-full border border-slate-300 px-6 py-3 text-[10px] tracking-[0.25em] text-slate-800 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#B8860B]";

export default function ConsentBanner() {
  const { bannerOpen, save, consent } = useConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState({ statistics: false, marketing: false, externalMedia: false });

  useEffect(() => {
    if (consent) {
      setChoices({
        statistics: Boolean(consent.statistics),
        marketing: Boolean(consent.marketing),
        externalMedia: Boolean(consent.externalMedia),
      });
    }
    if (!bannerOpen) setShowSettings(false);
  }, [consent, bannerOpen]);

  const acceptAll = () => save({ statistics: true, marketing: true, externalMedia: true });
  const necessaryOnly = () => save({ statistics: false, marketing: false, externalMedia: false });

  return (
    <AnimatePresence>
      {bannerOpen && (
        <>
          <motion.div
            data-testid="consent-banner"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="fixed inset-x-4 bottom-4 z-[95] sm:inset-x-6 sm:bottom-6"
          >
            <div className="mx-auto flex max-w-4xl flex-col gap-5 border border-[#D4AF37]/30 bg-white/95 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.14)] backdrop-blur-md sm:p-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <p className="f-mono mb-2 text-[10px] tracking-[0.35em] text-[#B8860B]">
                  PRIVACY & COOKIES
                </p>
                <p className="text-sm font-light leading-relaxed text-slate-600">
                  Diese Website verwendet ausschlie&szlig;lich technisch notwendige
                  Speichervorg&auml;nge. Optionale Dienste (Statistik, Marketing, externe
                  Medien) werden nur mit Ihrer Einwilligung aktiviert. Details in der{" "}
                  <a href="#/datenschutz" className="text-[#B8860B] underline underline-offset-2">
                    Datenschutzerkl&auml;rung
                  </a>
                  .
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  data-testid="consent-settings-button"
                  onClick={() => setShowSettings(true)}
                  className={BTN}
                >
                  EINSTELLUNGEN
                </button>
                <button
                  data-testid="consent-necessary-button"
                  onClick={necessaryOnly}
                  className={BTN}
                >
                  NUR NOTWENDIGE
                </button>
                <button
                  data-testid="consent-accept-all-button"
                  onClick={acceptAll}
                  className={BTN}
                >
                  ALLE AKZEPTIEREN
                </button>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {showSettings && (
              <motion.div
                data-testid="consent-settings-modal"
                className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div
                  className="absolute inset-0 bg-white/70 backdrop-blur-md"
                  onClick={() => setShowSettings(false)}
                />
                <motion.div
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 60, opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="relative flex max-h-[85vh] w-full max-w-2xl flex-col border border-[#D4AF37]/30 bg-white shadow-[0_40px_100px_rgba(15,23,42,0.18)]"
                >
                  <div className="flex items-center justify-between border-b border-slate-200 px-8 py-6">
                    <p className="f-mono text-[10px] tracking-[0.35em] text-[#B8860B]">
                      COOKIE-EINSTELLUNGEN
                    </p>
                    <button
                      data-testid="consent-settings-close"
                      onClick={() => setShowSettings(false)}
                      className="text-slate-400 transition-colors hover:text-slate-900"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div data-lenis-prevent className="flex-1 overflow-y-auto px-8 py-6">
                    {CATEGORIES.map((c) => (
                      <div key={c.key} className="border-b border-slate-100 py-5 last:border-b-0">
                        <div className="flex items-center justify-between gap-4">
                          <p className="f-display text-xl font-light text-slate-900">{c.name}</p>
                          {c.locked ? (
                            <span className="f-mono text-[9px] tracking-[0.25em] text-slate-400">
                              IMMER AKTIV
                            </span>
                          ) : (
                            <Toggle
                              testid={`consent-toggle-${c.key}`}
                              on={Boolean(choices[c.key])}
                              onChange={(v) => setChoices((s) => ({ ...s, [c.key]: v }))}
                            />
                          )}
                        </div>
                        <p className="mt-2 text-sm font-light leading-relaxed text-slate-500">
                          {c.desc}
                        </p>
                        <dl className="mt-3 space-y-1">
                          {c.details.map(([k, v]) => (
                            <div key={k} className="flex gap-3 text-xs">
                              <dt className="f-mono w-32 shrink-0 tracking-wide text-slate-400">
                                {k}
                              </dt>
                              <dd className="font-light text-slate-600">{v}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col gap-3 border-t border-slate-200 px-8 py-5 sm:flex-row sm:justify-end">
                    <button
                      data-testid="consent-save-button"
                      onClick={() => save(choices)}
                      className={BTN}
                    >
                      AUSWAHL SPEICHERN
                    </button>
                    <button
                      data-testid="consent-settings-accept-all"
                      onClick={acceptAll}
                      className={BTN}
                    >
                      ALLE AKZEPTIEREN
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
